import { useCallback, useState } from 'react';
import { cn } from '@localagent/ui/lib/utils';

interface CopyButtonProps {
  /** Text written to the clipboard when clicked. */
  text: string;
  /** Optional label shown next to the icon (defaults to "Copy"/"Copied"). */
  label?: string;
  className?: string;
}

/**
 * Small copy-to-clipboard button with transient "Copied" feedback. Shared so
 * views (e.g. Notifications) can offer a copy affordance without re-deriving the
 * navigator.clipboard + reset-timeout dance each time.
 */
export function CopyButton({ text, label = 'Copy', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    },
    [text]
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid="copy-button"
      className={cn(
        'rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent/50',
        className
      )}
      title="Copy to clipboard"
    >
      {copied ? 'Copied' : label}
    </button>
  );
}
